// Site-wide bilingual dictionary.
// Academic terminologies (SAT, AP, ISEE, school names, degree types) intentionally
// kept in English per client direction.

export const dict = {
    en: {
        nav: {
            home: "Home",
            k12: "K–12",
            college: "College",
            collegeMenuHeading: "College Admissions",
            collegeStrategy: "9–11th Grade Strategic Planning",
            collegeAdmissions: "Admissions Consulting",
            graduate: "Graduate",
            research: "Research",
            international: "International",
            cta: "Schedule a Consultation",
            ctaShort: "Book a Call",
            menu: "Menu",
            close: "Close",
        },
        footer: {
            brandSub: "Supernova Elite Education Destination, LLC",
            tagline: "Growth that has direction. Effort that comes back as outcome.",
            studio: "Studio",
            studioCity: "Livingston",
            studioState: "New Jersey 07039",
            studioMode: "In-person and online",
            contact: "Contact",
            wechat: "WeChat",
            hours: "Hours",
            hoursLine: "Mon — Sat",
            hoursMode: "By appointment",
            rights: "© 2018 Supernova Elite Education Destination · All Rights Reserved",
            privacy: "Privacy",
            terms: "Terms",
            language: "Language",
        },
        closingCta: {
            eyebrow: "Begin",
            title: "Start with a diagnostic conversation.",
            sub1: "No sales pitch. Just a real conversation.",
            sub2: "You'll leave having learned something about your child you didn't know before.",
            button: "Begin the Conversation",
        },
        breadcrumb: { home: "Home" },

        home: {
            hero: {
                line1: "Growth Mindset.",
                line2: "Clear Goals.",
                line3: "Consistent Effort.",
                tag: "K–12 · Ivy League · Top 15 Admissions",
                outcomesLabel: "Outcomes",
                stats: [
                    { num: "70%",  label: "Placement in Top 25" },
                    { num: "95%",  label: "Admitted to Reach School" },
                    { num: "100%", label: "Overall Acceptance Rate" },
                    { num: "3 : 1", label: "Student-to-Specialist Ratio" },
                ],
                body: [
                    "We help students become stronger applicants by first helping them become stronger people: more self-aware, more disciplined, more intellectually curious, and more confident in the direction they are building.",
                    "From K–12 private school admissions to Ivy League and Top 15 college applications, we pair senior-level admissions strategy with long-term student development. Every plan is personalized. Every milestone is intentional. Every application is built to show not just what a student has done, but who they are becoming.",
                ],
                ctaPrimary: "Schedule a Conversation",
                ctaSubLine1: "No sales pitch. A diagnostic conversation —",
                ctaSubLine2: "you leave with a clearer understanding of your child, their opportunities, and the next right move.",
                trustedEyebrow: "Trusted Approach",
                trustedQuote: "\u201CReal coaching, not transactional consulting. We work closely with a select number of families each year — and we know each student by name, story, and potential.\u201D",
                trustedAttribution: "— Founding Principal",
            },
            pillars: {
                eyebrow: "Approach",
                titleLine1: "Three convictions —",
                titleLine2: "the practice we built around them.",
                subtitle: "These principles shape every decision we make with a family, from the first diagnostic conversation to the final submitted application.",
                items: [
                    { title: "Personalized Guidance, Rooted in Potential", body: "We begin with the student, not a template. Through careful diagnosis, we identify academic strengths, motivation patterns, family dynamics, and the growth areas that matter most. From course selection to activity development to essays, the strategy is built around who the student truly is and where they can realistically grow." },
                    { title: "Goal-Oriented, Results-Driven", body: "Ambition needs structure. We translate big goals into a clear roadmap: school positioning, academic benchmarks, extracurricular depth, testing plans, essay timelines, and decision points. Students and parents always know what we are building, why it matters, and how each step connects to admissions outcomes." },
                    { title: "Holistic Development and Emotional Resilience", body: "Competitive admissions should not reduce a student to a resume. We help students build confidence, judgment, self-awareness, and resilience throughout the process. The goal is a stronger application — and a student who is more prepared for the school they hope to enter." },
                ],
            },
            assessment: {
                eyebrow: "Artifacts",
                titleA: { pre: "Start with ", italic: "diagnosis", post: "." },
                titleB: "Build with intention.",
                titleC: "Measure progress.",
                subtitle: "The artifacts below are not paperwork for the sake of process. They are the operating system behind a thoughtful admissions journey — helping students, parents, and counselors stay aligned from the first session through application submission.",
                footnote: "Six structured artifacts · one coordinated practice",
                items: [
                    { title: "Student Diagnostic Questionnaire", body: "A structured intake tool that maps the student's academic profile, extracurricular depth, motivation style, learning habits, personality, and family expectations before our first session." },
                    { title: "Initial Strategy Memo", body: "A concise counselor-written assessment of the student's current positioning, strongest growth opportunities, priority risks, and recommended next steps." },
                    { title: "Comprehensive Student Profile Report", body: "A full profile of the student's strengths, gaps, learning style, interests, character traits, and long-term direction — the foundation for school selection, activity planning, and narrative development." },
                    { title: "School Selection Strategy", body: "A curated, data-informed school list built around the student's academic level, personal fit, family priorities, institutional preferences, and realistic admissions range." },
                    { title: "Application Roadmap with Milestone Tracking", body: "A month-by-month execution plan that turns ambition into action, covering academics, testing, extracurricular development, essays, recommendations, interviews, and submission deadlines." },
                    { title: "Family Communication Audit", body: "A structured review of parent–child expectations, communication patterns, and pressure points, helping families support high standards without damaging trust." },
                ],
            },
            track: {
                eyebrow: "Track Record",
                titlePre: "Where our students",
                titleItalic: "actually went.",
                subtitle: "Class of 2026 admissions outcomes — Ivy League, Top 15 and Elite Technical, and K–12 Private Schools. Initials shown out of respect for privacy.",
                classOf: "Class of 2026",
                groups: { ivy: "Ivy League", top15: "Top 15 and Elite Technical", k12: "K–12 Private School" },
            },
        },

        pages: {
            k12: {
                eyebrow: "K–12 Private School Admissions",
                title: "The right school.",
                italic: "The right fit.",
                subtitle: "Every child deserves to be seen clearly. We make sure the right school sees them.",
                crumb: "K–12",
                whatWeDo: {
                    eyebrow: "What We Do",
                    title: "A complete advisory practice for private school admissions.",
                    subtitle: "From first conversation to enrollment day, our work spans every stage of the application year — testing, narrative, interviews, school selection, and decision support.",
                },
                phases: [
                    { label: "Discovery & Fit", items: [
                        "Personalized student assessment — strengths, interests, learning style",
                        "Family education philosophy — what matters most to your family",
                        "School selection guidance — curated for fit, not just rankings",
                        "School visit strategy — what to look for, what to ask",
                    ]},
                    { label: "Application & Preparation", items: [
                        "Timeline and step-by-step application plan",
                        "ISEE / SSAT preparation — diagnostic, study plan, practice tests",
                        "Parent and student essay guidance — brainstorming, drafting, editing",
                        "Interview and visit-day coaching — for parents and students",
                        "Recommender strategy — who to ask and how to approach them",
                    ]},
                    { label: "Submission & Beyond", items: [
                        "Final application review and submission management",
                        "Waitlist strategy and post-decision advising",
                        "Enrollment transition guidance — academic, social, logistical",
                    ]},
                ],
                whereGo: {
                    eyebrow: "Where Our Students Go",
                    title: "Day and boarding schools where our families have been admitted.",
                    subtitle: "A representative selection across the country — boarding programs and leading day schools in New Jersey, New York, and California.",
                },
                stories: {
                    eyebrow: "Student Stories",
                    title: "How four families found the right fit.",
                    subtitle: "At the K–12 level, the strongest applications rarely come from over-rehearsed children or over-polished resumes. They come from families who understand the child clearly, present that child honestly, and choose schools where the student can genuinely grow. Initials shown out of respect for privacy.",
                },
            },
            strategy: {
                eyebrow: "9–11th Grade Cultivation",
                title: "Real capability. Real growth.",
                italic: "The offer is the proof.",
                subtitle: "Skills, mindset, substance — built through three years of authentic development, not just application tactics.",
                crumbCollege: "College",
                crumbSelf: "9–11th Grade Strategic Planning",
            },
            admissions: {
                eyebrow: "College Admissions Consulting",
                title: "Full-cycle college",
                italic: "admissions consulting.",
                subtitle: "Expert-led, full-service guidance for students applying to top US universities.",
                crumbCollege: "College",
                crumbSelf: "Admissions Consulting",
            },
            graduate: {
                eyebrow: "Graduate Admissions",
                title: "A focused advisory practice for",
                italic: "graduate admissions.",
                subtitle: null,
                crumb: "Graduate",
            },
            research: {
                eyebrow: "Research Mentorship",
                title: "From curiosity to publication.",
                italic: "Real research. Real output.",
                subtitle: "A research paper is more than a credential — it's proof your child can think at the level colleges are looking for.",
                crumb: "Research",
            },
            international: {
                eyebrow: "International Student Support",
                title: "A softer landing.",
                italic: "A stronger start.",
                subtitle: "We help international students settle into American life — visas, host families, and a guardian on the ground.",
                crumb: "International",
                services: [
                    { h: "Visa Support", sub: "F-1 student visa, end to end.", items: [
                        "Application strategy aligned with program start date and travel needs",
                        "Document preparation — financial evidence, sponsor letters, transcripts, school correspondence",
                        "Interview preparation — practice prompts, concise study plans, grounded answers",
                        "Status tracking, renewals, travel signatures, and transfer coordination",
                    ], footnote: "We provide educational consulting support; visa decisions are made solely by U.S. government agencies." },
                    { h: "Homestay Program", sub: "A second home, carefully matched.", items: [
                        "Detailed intake on routine, diet, commute, study habits, and house expectations",
                        "Placement with hosts experienced in welcoming international students",
                        "Orientation on house rules, communication norms, and points of contact",
                        "Ongoing check-ins and calm mediation when adjustments are needed",
                    ]},
                    { h: "Guardian Program", sub: "A trusted local adult, on call.", items: [
                        "Designated U.S.-based guardian for emergencies and required school meetings",
                        "Authorized pickup, medical visits, and school conferences when parents are abroad",
                        "Liaison with school nurses, deans, and residential offices",
                        "Real-time updates to parents through the channels you already use",
                    ]},
                ],
            },
            contact: {
                crumb: "Schedule a Consultation",
            },
            privacy: {
                eyebrow: "Legal",
                title: "Privacy",
                italic: "Policy.",
                subtitle: "How we collect, use, and protect the information you share with us.",
                crumb: "Privacy",
            },
            terms: {
                eyebrow: "Legal",
                title: "Terms and",
                italic: "Conditions.",
                subtitle: "The agreement between your family and Supernova when you engage our services.",
                crumb: "Terms",
            },
        },
    },

    zh: {
        nav: {
            home: "首页",
            k12: "K–12 升学",
            college: "本科",
            collegeMenuHeading: "本科升学",
            collegeStrategy: "9–11 年级战略规划",
            collegeAdmissions: "本科申请咨询",
            graduate: "研究生",
            research: "科研项目",
            international: "国际学生",
            cta: "预约咨询",
            ctaShort: "预约咨询",
            menu: "菜单",
            close: "关闭",
        },
        footer: {
            brandSub: "Supernova Elite Education Destination, LLC",
            tagline: "让成长有方向，让努力有回响。",
            studio: "工作室",
            studioCity: "Livingston",
            studioState: "New Jersey 07039",
            studioMode: "线下与线上",
            contact: "联系我们",
            wechat: "微信",
            hours: "时间",
            hoursLine: "周一 — 周六",
            hoursMode: "预约制",
            rights: "© 2018 Supernova Elite Education Destination · 版权所有",
            privacy: "隐私政策",
            terms: "服务条款",
            language: "语言",
        },
        closingCta: {
            eyebrow: "开始",
            title: "从一次诊断式对话开始。",
            sub1: "不推销，只是一次真实的交流。",
            sub2: "你会带着对孩子全新的认知离开这场对话。",
            button: "开始对话",
        },
        breadcrumb: { home: "首页" },

        home: {
            hero: {
                line1: "让成长有方向，",
                line2: "让努力有回响。",
                line3: "",
                tag: "K–12 · 常春藤 · 美国前 25 名校",
                outcomesLabel: "录取结果",
                stats: [
                    { num: "70%",  label: "进入美国前 25 大学" },
                    { num: "95%",  label: "被冲刺校录取" },
                    { num: "100%", label: "申请成功率" },
                    { num: "3 : 1", label: "学生与专家配比" },
                ],
                body: [
                    "我们以个性化的指导与真诚的洞察，帮助学生构建有深度的学术与课外档案，讲述属于他们自己的故事。",
                    "以清晰的方向和坚定的信心走完升学之路——从 K–12 私立学校，到常春藤盟校与美国前 25 大学。",
                ],
                ctaPrimary: "预约一次对话",
                ctaSubLine1: "不是推销。这是一次诊断式的交流 ——",
                ctaSubLine2: "聊完之后，你会对孩子有更清晰的认识。",
                trustedEyebrow: "我们的理念",
                trustedQuote: "\u201C我们不是留学公司，是真正的陪跑者。每年我们只服务 20 到 30 个家庭——每一个家庭，我们都记得他们的名字。\u201D",
                trustedAttribution: "—— 主理人",
            },
            pillars: {
                eyebrow: "理念",
                titleLine1: "三大信念 ——",
                titleLine2: "支撑起整个服务体系。",
                subtitle: "我们围绕这三个信念，搭建了整个服务体系。从第一次对话到最终提交申请，每一个决定都由此而生。",
                items: [
                    { title: "因材施教，挖掘闪光点", body: "我们根据每个学生独特的优势与志向提供指导。对美国升学体系的深度理解，转化为个性化的策略——从选课到文书——与你的孩子将来成为什么样的人、要去哪里，紧紧契合。我们不套模板，为每一个学生量身打造。" },
                    { title: "目标清晰，结果导向", body: "从第一天起就有明确的目标。我们设定具体的里程碑和战略时间表，指向可衡量的结果。每一步都有用意，每一个决定都有依据——一条从起点通往目标的清晰路径。每一个里程碑都被追踪，每一个决定都有理由。" },
                    { title: "身心健康，全面发展", body: "学业之外，我们同样看重品格成长、情绪韧性与心理健康。升学之路应当是孩子建立自信与自我认知的过程，而不是焦虑的来源——在一个被支持的环境里，他们才能真正成长。孩子会在这个过程中变得更强大，而不仅仅是撑过去。" },
                ],
            },
            assessment: {
                eyebrow: "评估工具",
                titleA: { pre: "从", italic: "诊断", post: "开始，" },
                titleB: "而不是套模板。",
                titleC: "",
                subtitle: "每个学生的路径都是独特的。以下六个框架贯穿我们的合作——从第一次见面，到申请提交。",
                footnote: "六套结构化框架 · 一个协同的工作方式",
                items: [
                    { title: "学生诊断问卷", body: "一份结构化的问卷，在第一次见面之前，全面了解学生的学术状况、课外深度、内在驱动力以及家庭关系。" },
                    { title: "季度回顾", body: "我们一起完成的季度回顾——追踪学业进展、活动发展、叙事清晰度以及每个阶段的心理状态。" },
                    { title: "综合学生报告", body: "对孩子的优势、短板、学习风格与长期方向的完整画像。" },
                    { title: "选校策略", body: "围绕孩子个人特点和目标，精心筛选、数据支撑的选校名单。" },
                    { title: "升学路线图与里程碑追踪", body: "从九年级到申请提交，逐月推进的结构化执行计划。" },
                    { title: "家庭沟通评估", body: "每季度回顾一次家长与孩子之间的沟通模式和期望是否对齐——帮助家庭在压力最大的几年里，不伤害亲子关系地走过。" },
                ],
            },
            track: {
                eyebrow: "过往成果",
                titlePre: "孩子们",
                titleItalic: "去到了哪里。",
                subtitle: "Class of 2026 录取结果——常春藤、Top 15 与精英理工院校、K–12 私立学校。出于隐私保护，以首字母呈现。",
                classOf: "Class of 2026",
                groups: { ivy: "常春藤盟校", top15: "Top 15 与精英理工", k12: "K–12 私立学校" },
            },
        },

        pages: {
            k12: {
                eyebrow: "K–12 私立学校升学",
                title: "选对学校，",
                italic: "孩子自会绽放。",
                subtitle: "每个孩子都值得被看见。我们让对的学校真正看见他。",
                crumb: "K–12 升学",
                whatWeDo: {
                    eyebrow: "我们做什么",
                    title: "完整的私立学校升学顾问服务。",
                    subtitle: "从第一次对话到入学，我们的工作覆盖申请季的每一个阶段——标化考试、申请叙事、面试、选校与决策支持。",
                },
                phases: [
                    { label: "探索与匹配", items: [
                        "个性化学生评估 —— 优势、兴趣、学习风格",
                        "家庭教育理念 —— 对你家来说最重要的是什么",
                        "选校指导 —— 以契合度为核心，而非排名",
                        "校园访校策略 —— 看什么、问什么",
                    ]},
                    { label: "申请与备考", items: [
                        "时间线与分步推进的申请计划",
                        "ISEE / SSAT 备考 —— 诊断、学习计划、模拟考试",
                        "家长与学生文书指导 —— 头脑风暴、初稿、修改",
                        "面试与访校日辅导 —— 面向家长和学生",
                        "推荐人策略 —— 找谁、怎么沟通",
                    ]},
                    { label: "提交与入学后", items: [
                        "最终申请审阅与提交管理",
                        "Waitlist 应对策略与录取后建议",
                        "入学过渡指导 —— 学术、社交、生活适应",
                    ]},
                ],
                whereGo: {
                    eyebrow: "孩子们去了哪里",
                    title: "我们的家庭被录取的走读与寄宿学校。",
                    subtitle: "覆盖全美的代表性选择——顶尖寄宿项目，以及新泽西、纽约与加州的领先走读学校。",
                },
                stories: {
                    eyebrow: "学生故事",
                    title: "四个家庭如何找到对的契合。",
                    subtitle: "在 K–12 阶段，最强有力的申请，往往不来自被过度排练的孩子或被过度修饰的简历。它们来自看得清孩子的家庭、真实呈现孩子的家庭，以及为孩子选择真正能成长的学校的家庭。出于隐私保护，以首字母呈现。",
                },
            },
            strategy: {
                eyebrow: "9–11 年级陪跑",
                title: "激发学生，成就最好的自己。",
                italic: "录取通知书，就是最好的证明。",
                subtitle: "自驱力、能力、内核 —— 通过三年的陪伴和带领不断历练得来，而不是申请季前临时抱佛脚的技巧。",
                crumbCollege: "本科",
                crumbSelf: "9–11 年级战略规划",
            },
            admissions: {
                eyebrow: "本科升学咨询",
                title: "全流程的",
                italic: "本科申请咨询。",
                subtitle: "为冲刺美国顶尖大学的学生，提供专家主导的全程指导。",
                crumbCollege: "本科",
                crumbSelf: "本科申请咨询",
            },
            graduate: {
                eyebrow: "研究生申请",
                title: "专注的",
                italic: "研究生申请咨询。",
                subtitle: null,
                crumb: "研究生",
            },
            research: {
                eyebrow: "科研项目",
                title: "科研的起点是好奇，",
                italic: "论文是水到渠成的成果。",
                subtitle: "由一线研究导师带领，进入真正的科研项目，探索前沿问题。一篇研究论文不只是一张证书——它证明你的孩子具备了顶尖大学所看重的思维方式。",
                crumb: "科研项目",
            },
            international: {
                eyebrow: "国际学生支持",
                title: "落脚有人接，",
                italic: "日常有人管。",
                subtitle: "我们帮助国际学生融入美国生活——签证、寄宿家庭、本地监护人，一站式全程支持。",
                crumb: "国际学生",
                services: [
                    { h: "签证支持", sub: "F-1 学生签证，全程陪同。", items: [
                        "结合开学日期与出行需求的申请策略",
                        "材料准备 —— 财力证明、担保信、成绩单、学校往来邮件",
                        "面签辅导 —— 模拟问题、清晰的学习计划、扎实的回答",
                        "状态追踪、续签与转学协调",
                    ], footnote: "我们提供教育咨询支持；签证决定由美国政府机构独立作出。" },
                    { h: "寄宿家庭项目", sub: "第二个家，精心匹配。", items: [
                        "对作息、饮食、通勤、学习习惯与家庭期望的详细评估",
                        "匹配有接待国际学生经验的寄宿家庭",
                        "家规、沟通方式与联系人的入住引导",
                        "持续回访与平和的适应期调解",
                    ]},
                    { h: "本地监护人项目", sub: "随时在身边的本地成年人。", items: [
                        "在美国指定监护人，处理紧急情况与学校要求的家长出席",
                        "在家长不在美国时，授权接送、医疗就诊、学校会议",
                        "与校医、学部主任、宿舍管理处对接",
                        "通过你熟悉的渠道，向家长同步反馈",
                    ]},
                ],
            },
            contact: {
                crumb: "预约咨询",
            },
            privacy: {
                eyebrow: "法律条款",
                title: "隐私",
                italic: "政策。",
                subtitle: "我们如何收集、使用并保护您与我们分享的信息。",
                crumb: "隐私政策",
            },
            terms: {
                eyebrow: "法律条款",
                title: "服务",
                italic: "条款。",
                subtitle: "当您选择我们的服务时，您的家庭与 Supernova 之间的约定。",
                crumb: "服务条款",
            },
        },
    },
};
